import React from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Nav variants
export type NavVariant = 'horizontal' | 'vertical';

// Nav props
export interface NavProps {
  /**
   * The variant of the navigation
   * @default 'horizontal'
   */
  variant?: NavVariant;
  
  /**
   * The content of the navigation
   */
  children: React.ReactNode;
  
  /**
   * Additional class name
   */
  className?: string;
  
  /**
   * ARIA label for the navigation
   * @default 'Main navigation'
   */
  ariaLabel?: string;
}

// Nav item props
export interface NavItemProps {
  /**
   * Whether the item is active
   * @default false
   */
  active?: boolean;
  
  /**
   * Whether the item is disabled
   * @default false
   */
  disabled?: boolean;
  
  /**
   * The content of the item
   */
  children: React.ReactNode;
  
  /**
   * Click handler for the item
   */
  onClick?: (event: React.MouseEvent<HTMLLIElement>) => void;
  
  /**
   * Additional class name
   */
  className?: string;
  
  /**
   * The URL for the item
   */
  href?: string;
  
  /**
   * The target for the link
   */
  target?: string;
  
  /**
   * The rel attribute for the link
   */
  rel?: string;
}

// Styled components
const StyledNav = styled.nav<{ variant: NavVariant }>`
  font-family: ${typography.fontFamily.primary};
  
  ${props => props.variant === 'horizontal' && css`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ${spacingSystem.spacing[8]} 0;
  `}
  
  ${props => props.variant === 'vertical' && css`
    display: flex;
    flex-direction: column;
    padding: ${spacingSystem.spacing[8]} 0;
  `}
`;

const NavList = styled.ul<{ variant: NavVariant }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  
  ${props => props.variant === 'horizontal' && css`
    flex-direction: row;
    align-items: center;
  `}
  
  ${props => props.variant === 'vertical' && css`
    flex-direction: column;
    width: 100%;
  `}
`;

const NavListItem = styled.li<{ active?: boolean; disabled?: boolean; variant: NavVariant }>`
  margin: 0;
  
  ${props => props.variant === 'horizontal' && css`
    margin-right: ${spacingSystem.spacing[16]};
    
    &:last-child {
      margin-right: 0;
    }
  `}
  
  ${props => props.variant === 'vertical' && css`
    margin-bottom: ${spacingSystem.spacing[8]};
    width: 100%;
    
    &:last-child {
      margin-bottom: 0;
    }
  `}
  
  ${props => props.active && css`
    font-weight: ${typography.fontWeight.medium};
    
    a {
      color: ${colors.primary[700]};
      
      &::after {
        content: '';
        display: block;
        height: 2px;
        background-color: ${colors.primary[500]};
        margin-top: 4px;
        
        ${props.variant === 'vertical' && css`
          width: 4px;
          height: 100%;
          position: absolute;
          left: 0;
          top: 0;
          margin-top: 0;
        `}
      }
    }
  `}
  
  ${props => props.disabled && css`
    opacity: 0.5;
    pointer-events: none;
  `}
`;

const NavLink = styled.a<{ active?: boolean; disabled?: boolean; variant: NavVariant }>`
  color: ${props => props.active ? colors.primary[700] : colors.neutral[700]};
  text-decoration: none;
  font-size: ${typography.fontSize.md};
  padding: ${props => props.variant === 'vertical' ? `${spacingSystem.spacing[8]} ${spacingSystem.spacing[12]}` : '0'};
  display: block;
  position: relative;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${colors.primary[500]};
  }
  
  ${props => props.disabled && css`
    color: ${colors.neutral[400]};
    cursor: not-allowed;
    
    &:hover {
      color: ${colors.neutral[400]};
    }
  `}
`;

/**
 * Nav component for navigation menus
 */
export const Nav: React.FC<NavProps> & {
  Item: React.FC<NavItemProps>;
} = ({
  variant = 'horizontal',
  children,
  className,
  ariaLabel = 'Main navigation',
  ...rest
}) => {
  return (
    <StyledNav
      variant={variant}
      className={className}
      aria-label={ariaLabel}
      {...rest}
    >
      <NavList variant={variant}>
        {children}
      </NavList>
    </StyledNav>
  );
};

/**
 * NavItem component for navigation items
 */
export const NavItem: React.FC<NavItemProps> = ({
  active = false,
  disabled = false,
  children,
  onClick,
  className,
  href,
  target,
  rel,
  ...rest
}) => {
  // Get the variant from the parent Nav context
  const variant = React.useContext(NavContext);
  
  const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    
    if (onClick) {
      onClick(event);
    }
  };
  
  return (
    <NavListItem
      active={active}
      disabled={disabled}
      variant={variant}
      onClick={handleClick}
      className={className}
      {...rest}
    >
      {href ? (
        <NavLink
          href={href}
          target={target}
          rel={rel}
          active={active}
          disabled={disabled}
          variant={variant}
        >
          {children}
        </NavLink>
      ) : (
        children
      )}
    </NavListItem>
  );
};

// Create a context for the Nav variant
const NavContext = React.createContext<NavVariant>('horizontal');

// Attach NavItem as a static property of Nav
Nav.Item = NavItem;

export default Nav;